import { t } from "../../../features/store-language/i18n.ts";
import type { AgencyOtpTimingResponse } from "./agencyOtpTiming.ts";
import { HttpService } from "../../../utils/DesigningHttpService.js";
import { agencySessionKey, getAgencySession, setAgencySession } from "../../../utils/agencySession.ts";

const http = new HttpService();
function recipient(value: string): { mobile: string; email: string } {
  return value.includes("@") ? { mobile: "", email: value.trim().toLowerCase() } : { mobile: value, email: "" };
}

export type AgencyRole = "Manager" | "Staff";
export type PortalRole = AgencyRole | "CompanySalesAgent";
export type AgencyChoice = { salesAgencyId: number; membershipId: number; title: string; role: PortalRole };
export type AgencyContext = { userId: string; salesAgencyId: string; membershipId: string; role: string; displayName: string; mobile: string; email?: string; userName?: string; agencyTitle: string; canViewInventory: boolean };

export async function agencyBootstrap(): Promise<{ isAuthenticated: boolean; csrfToken: string }> {
  return http.agencyBootstrap();
}

async function agencyPost(path: string, body: unknown): Promise<unknown> {
  const bootstrap = await agencyBootstrap();
  const result = await http.postasync(path, body, { headers: { "X-Portal-Csrf": bootstrap.csrfToken } });
  return result.data;
}

export async function acceptAgencyInvitation(values: {
  token: string; mobile: string; verificationCode: string;
  displayName: string; password: string; passwordConfirmation: string;
}): Promise<{ isAuthenticated: boolean }> {
  const result = await agencyPost("/catalog/agency-account/accept-invitation", { ...values, ...recipient(values.mobile) }) as { isAuthenticated: boolean };
  if (!result.isAuthenticated) setAgencySession(null);
  return result;
}

export async function agencyInvitationContext(token: string): Promise<{ languageCode: string; method: "email" | "sms" }> {
  return await agencyPost("/catalog/agency-account/invitation-context", { token }) as { languageCode: string; method: "email" | "sms" };
}

export async function verifyAgencyInvitationCode(token: string, mobile: string, verificationCode: string): Promise<boolean> {
  const result = await agencyPost("/catalog/agency-account/verify-invitation-code", {
    token, ...recipient(mobile), verificationCode,
  }) as { verified?: boolean } | null;
  return result?.verified === true;
}

export type AgencyVerificationCodeResponse = AgencyOtpTimingResponse & {
  deliveryFailed: boolean;
  deliveryFailureMessage?: string;
};

export async function sendAgencyVerification(token: string, mobile: string): Promise<AgencyVerificationCodeResponse> {
  return await agencyPost("/catalog/agency-account/invitation-code", { token, ...recipient(mobile) }) as AgencyVerificationCodeResponse;
}

export async function agencyLogin(mobile: string, password: string): Promise<{
  isAuthenticated: boolean;
}> {
  return await agencyPost("/catalog/agency-account/login", { ...recipient(mobile), password }) as {
    isAuthenticated: boolean;
  };
}

export async function agencyContext(): Promise<AgencyContext> {
  const requestedSession = agencySessionKey();
  const result = await http.getasync("/catalog/agency-account/context");
  const current = getAgencySession();
  if (requestedSession !== agencySessionKey()
      && (current?.userId !== result.data.userId || current?.membershipId !== result.data.membershipId
        || current?.role !== result.data.role || current?.salesAgencyId !== result.data.salesAgencyId)) {
    throw new Error(t("نشست ورود تغییر کرده است؛ اطلاعات حساب را دوباره دریافت کنید."));
  }
  setAgencySession(result.data);
  return result.data;
}

export async function agencyLogout(): Promise<void> {
  await agencyPost("/catalog/agency-account/logout", {});
  setAgencySession(null);
  localStorage.removeItem("user");
  localStorage.removeItem("jwt");
}

export async function changeAgencyPassword(currentPassword: string, newPassword: string): Promise<void> {
  await agencyPost("/catalog/agency-account/change-password", { currentPassword, newPassword });
  setAgencySession(null);
  localStorage.removeItem("user");
  localStorage.removeItem("jwt");
}

export async function requestAgencyContact(value: string, currentPassword: string): Promise<AgencyOtpTimingResponse & { verificationId: string }> {
  if (!getAgencySession()) throw new Error(t("نشست معتبر نیست."));
  return await agencyPost("/catalog/agency-account/contact/request", { ...recipient(value), currentPassword }) as AgencyOtpTimingResponse & { verificationId: string };
}

export async function confirmAgencyContact(verificationId: string, code: string,
  value: string, isEmail: boolean): Promise<boolean> {
  if (!getAgencySession()) throw new Error(t("نشست معتبر نیست."));
  const requestedSession = agencySessionKey();
  await agencyPost("/catalog/agency-account/contact/confirm", { verificationId, code });
  if (requestedSession !== agencySessionKey()) return false;
  const current = getAgencySession();
  if (!current) return false;
  setAgencySession({ ...current, [isEmail ? "email" : "mobile"]: value });
  return true;
}

export type AgencyMember = { email?: string; id: number; displayName: string; mobile?: string; role: AgencyRole; isActive: boolean; hasOrders: boolean; version: string };

function requireAgencyManager(): void {
  if (getAgencySession()?.role !== "Manager") {
    throw new Error(t("دسترسی به این بخش فقط برای مدیر نمایندگی مجاز است."));
  }
}

export async function deleteAgencyMember(member: AgencyMember): Promise<void> {
  requireAgencyManager();
  await agencyPost(`/catalog/agency-members/${member.id}/delete`, { version: member.version });
}
export type AgencyInvitation = { email?: string; emailStatus?: string; id: string; mobile: string; status: "Pending" | "Accepted" | "Revoked"; expiresAt: string; smsRequestedAt?: string; smsAcceptedAt?: string; smsAttempts: number };

export async function agencyMembers(): Promise<AgencyMember[]> {
  requireAgencyManager();
  return (await http.getasync("/catalog/agency-members")).data;
}

export async function createAgencyMember(values: {
  displayName: string;
  mobile: string;
  password: string;
  passwordConfirmation: string;
  role: AgencyRole;
}): Promise<void> {
  requireAgencyManager();
  await agencyPost("/catalog/agency-members", values);
}

export async function agencyInvitations(): Promise<AgencyInvitation[]> {
  requireAgencyManager();
  return (await http.getasync("/catalog/agency-invitations?all=true")).data;
}

export async function inviteAgencyMember(mobile: string, role: AgencyRole, requestId: string): Promise<void> {
  requireAgencyManager();
  await agencyPost("/catalog/agency-invitations", { ...recipient(mobile), role, requestId });
}

export async function changeAgencyMember(member: AgencyMember, values: {
  displayName: string;
  mobile: string;
  email: string;
  newPassword: string;
  passwordConfirmation: string;
  role: AgencyRole;
  isActive: boolean;
}): Promise<void> {
  requireAgencyManager();
  const bootstrap = await agencyBootstrap();
  await http.putasync(`/catalog/agency-members/${member.id}`, { ...values, version: member.version },
    { headers: { "X-Portal-Csrf": bootstrap.csrfToken } });
}

export async function manageAgencyInvitation(id: string, action: "link" | "sms" | "email" | "revoke"): Promise<{ link?: string }> {
  requireAgencyManager();
  return (await agencyPost(`/catalog/agency-invitations/${encodeURIComponent(id)}/${action}`, action === "email" ? { requestId: crypto.randomUUID() } : {})) as { link?: string } ?? {};
}

export async function sendAgencyLoginCode(mobile: string): Promise<AgencyOtpTimingResponse> {
  return await agencyPost("/catalog/agency-account/login-code", recipient(mobile)) as AgencyOtpTimingResponse;
}

export async function agencyOtpLogin(mobile: string, code: string): Promise<{
  isAuthenticated: boolean;
}> {
  return await agencyPost("/catalog/agency-account/login-otp", { ...recipient(mobile), code }) as {
    isAuthenticated: boolean;
  };
}
